import { useApolloClient } from '@apollo/client';
import {
  ConversationMessageDispatchStatus,
  ConversationSmsDirection,
} from '@provision/conversation-types';
import { ConversationParticipation } from '@provision/entity-types';
import { useMutation, useQuery } from '@provision/graphql-client';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { config } from 'src/core/config';
import { wsEventBus } from 'src/core/realtime-connection';
import { ConversationParticipantItem } from 'src/graphql';
import { DEFAULT_MESSAGE_LIMIT } from 'src/graphql/entities/conversation-message/constants';
import { GET_CONVERSATION_MESSAGES_QUERY } from 'src/graphql/entities/conversation-message/conversation-message.operations';
import { getConversationMessagesQueryVariables } from 'src/graphql/entities/conversation-message/hooks/use-conversation-messages';
import {
  getEffectiveDispatchStatus,
  parseDispatchFailureReason,
} from 'src/pages/Conversations/Chat/ChatMessage/dispatch-status/dispatch-status.utils';
import { useSmsSwitchVisibility } from 'src/pages/Conversations/Chat/InputField/hooks/use-sms-switch-visibility';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import {
  SmsSessionContext,
  type SmsSessionContextValue,
  type StartedSmsParticipation,
} from './sms-session-context';
import { groupMessagesBySession, type TaggedMessage } from './sms-session-grouping';
import {
  END_SMS_SESSION_MUTATION,
  type EndSmsSessionMutationResult,
  type EndSmsSessionMutationVars,
  GET_OPEN_SMS_SESSION_QUERY,
  getOpenSmsSessionQueryVariables,
  START_SMS_SESSION_MUTATION,
  type StartSmsSessionMutationResult,
  type StartSmsSessionMutationVars,
} from './sms-session.operations';
import { patchSessionOpen, useSmsSessionRealtime } from './use-sms-session-realtime';

interface RawMessageRecord {
  conversationMessageId: string;
  createdAt: string;
  body?: string | null;
  partnerId?: number | null;
  senderChannel?: { id: string } | null;
  attachments?: { fileName?: string | null; fileKey?: string | null }[] | null;
  messageDispatches?:
    | {
        dispatchStatus?: string | null;
        customAttributes?: string | null;
        createdAt?: string | null;
        updatedAt?: string | null;
      }[]
    | null;
  conversationSmsRecords?:
    | {
        conversationSmsSessionId: string;
        direction: string;
        externalMessageId: string | null;
      }[]
    | null;
}

interface RawQueryData {
  getConversationMessage: {
    records: RawMessageRecord[] | null;
    metadata: { offset: number; totalRecords: number };
  };
}

function useTaggedMessages(
  conversationId: string,
  skip: boolean,
): { messages: TaggedMessage[]; loading: boolean } {
  const variables = useMemo(
    () =>
      getConversationMessagesQueryVariables({
        filters: [{ attribute: 'conversationId', operator: '=', value: conversationId }],
        limit: DEFAULT_MESSAGE_LIMIT,
      }),
    [conversationId],
  );

  const { data, loading } = useQuery<RawQueryData>(GET_CONVERSATION_MESSAGES_QUERY, {
    variables,
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    skip: skip || !conversationId,
  });

  const messages = useMemo<TaggedMessage[]>(() => {
    const records = data?.getConversationMessage?.records ?? [];
    return records
      .filter((msg) => msg.conversationMessageId && msg.createdAt)
      .map((msg) => {
        const smsRecords = msg.conversationSmsRecords ?? [];
        // A wire id on the OUTGOING row means Twilio accepted the message: a dispatch still
        // `dispatching` next to it is stale bookkeeping, not a lost send. An explicit `failed` row always counts.
        const acceptedByTwilio = smsRecords.some(
          (sms) =>
            (sms.direction as ConversationSmsDirection) ===
              ConversationSmsDirection.Outgoing && Boolean(sms.externalMessageId),
        );
        const failedDispatch = (msg.messageDispatches ?? []).find((dispatch) => {
          if (
            (dispatch.dispatchStatus as ConversationMessageDispatchStatus) ===
            ConversationMessageDispatchStatus.Failed
          ) {
            return true;
          }
          return (
            !acceptedByTwilio &&
            getEffectiveDispatchStatus({
              dispatchStatus: dispatch.dispatchStatus ?? '',
              createdAt: dispatch.createdAt ?? msg.createdAt,
              updatedAt: dispatch.updatedAt ?? '',
            }) === ConversationMessageDispatchStatus.Failed
          );
        });

        return {
          conversationMessageId: msg.conversationMessageId,
          createdAt: msg.createdAt,
          body: msg.body ?? null,
          senderChannelId: msg.senderChannel?.id ?? null,
          attachments: (msg.attachments ?? [])
            .filter((attachment) => attachment?.fileName)
            .map((attachment) => ({
              fileName: attachment.fileName!,
              url:
                attachment.fileKey && msg.partnerId
                  ? `${config.s3AttachmentsUrl}${msg.partnerId}/${conversationId}/${attachment.fileKey}`
                  : null,
            })),
          dispatchFailed: Boolean(failedDispatch),
          dispatchFailureReason: failedDispatch
            ? parseDispatchFailureReason(failedDispatch.customAttributes)
            : null,
          smsTags: smsRecords.map((sms) => ({
            conversationSmsSessionId: sms.conversationSmsSessionId,
            direction: sms.direction,
          })),
        };
      });
  }, [data, conversationId]);

  return { messages, loading };
}

interface SmsSessionProviderProps {
  conversationId: string;
  participants: ConversationParticipantItem[];
  requesterFirstName: string | null;
  children: React.ReactNode;
}

function buildSendersByChannelId(
  participants: ConversationParticipantItem[],
  participations: ConversationParticipation[],
): ReadonlyMap<string, string> {
  const namesByParticipantId = new Map<string, string>();
  for (const participant of participants) {
    const fullName = participant.client?.fullName ?? participant.platformUser?.fullName;
    if (participant.conversationParticipantId && fullName) {
      namesByParticipantId.set(participant.conversationParticipantId, fullName);
    }
  }

  const map = new Map<string, string>();
  for (const participation of participations) {
    const channel = participation.channel;
    const name = channel?.conversationParticipantId
      ? namesByParticipantId.get(channel.conversationParticipantId)
      : undefined;
    if (channel?.id && name) map.set(channel.id, name);
  }
  return map;
}

export const SmsSessionProvider = ({
  conversationId,
  participants,
  requesterFirstName,
  children,
}: SmsSessionProviderProps): React.JSX.Element => {
  const client = useApolloClient();

  const conversationParticipations = useChatCommonData((s) => s.conversationParticipations);
  const sendersByChannelId = useMemo(
    () => buildSendersByChannelId(participants, conversationParticipations),
    [participants, conversationParticipations],
  );

  const {
    showSmsSwitch,
    hasOpenSession,
    openSessionId,
    openSessionChannelId,
    sessionOpenedAt: openedAt,
    smsDisabledReason,
  } = useSmsSwitchVisibility(conversationId);

  const [endSmsMutation, { loading: isEnding }] = useMutation<
    EndSmsSessionMutationResult,
    EndSmsSessionMutationVars
  >(END_SMS_SESSION_MUTATION);

  const [startSmsMutation] = useMutation<
    StartSmsSessionMutationResult,
    StartSmsSessionMutationVars
  >(START_SMS_SESSION_MUTATION);

  const startSession = useCallback(
    async (conversationChannelId?: string): Promise<StartedSmsParticipation | null> => {
      const { data } = await startSmsMutation({
        variables: { input: { conversationId, conversationChannelId } },
        // Patch the open-session cache so the panel appears without waiting for
        // the WS entity event (whose patch of the same entry is idempotent).
        onCompleted(result) {
          patchSessionOpen(
            client,
            result.startSmsSession.conversationSmsSession,
            conversationId,
          );
        },
      });
      return data?.startSmsSession.smsParticipation ?? null;
    },
    [startSmsMutation, client, conversationId],
  );

  const endSession = useCallback(async () => {
    if (!openSessionId) return;
    await endSmsMutation({
      variables: { input: { conversationSmsSessionId: openSessionId } },
      onCompleted() {
        client.writeQuery({
          query: GET_OPEN_SMS_SESSION_QUERY,
          variables: getOpenSmsSessionQueryVariables(conversationId),
          data: { getConversationSmsSession: { records: [] } },
        });
      },
    });
  }, [openSessionId, endSmsMutation, client, conversationId]);

  const [overlayMessages, setOverlayMessages] = useState<TaggedMessage[]>([]);
  const prevConversationIdRef = useRef(conversationId);
  if (prevConversationIdRef.current !== conversationId) {
    prevConversationIdRef.current = conversationId;
    setOverlayMessages([]);
  }

  const handlePairComplete = useCallback((msg: TaggedMessage) => {
    setOverlayMessages((prev) => {
      if (prev.some((m) => m.conversationMessageId === msg.conversationMessageId)) {
        return prev;
      }
      return [...prev, msg];
    });
  }, []);

  useSmsSessionRealtime({
    client,
    wsEventBus,
    conversationId,
    showSmsSwitch,
    onPairComplete: handlePairComplete,
  });

  const { messages: cacheMessages, loading: messagesLoading } = useTaggedMessages(
    conversationId,
    !showSmsSwitch,
  );

  const mergedMessages = useMemo<TaggedMessage[]>(() => {
    if (overlayMessages.length === 0) return cacheMessages;

    const overlayById = new Map(overlayMessages.map((m) => [m.conversationMessageId, m]));

    const tagged = cacheMessages.map((m) => {
      const overlay = overlayById.get(m.conversationMessageId);
      return overlay && m.smsTags.length === 0 ? { ...m, smsTags: overlay.smsTags } : m;
    });

    const cacheIds = new Set(cacheMessages.map((m) => m.conversationMessageId));

    const newOverlay = overlayMessages.filter((m) => !cacheIds.has(m.conversationMessageId));

    return newOverlay.length === 0 ? tagged : [...tagged, ...newOverlay];
  }, [cacheMessages, overlayMessages]);

  const sessionGroups = useMemo(
    () => groupMessagesBySession(mergedMessages, openSessionId),
    [mergedMessages, openSessionId],
  );

  const value = useMemo<SmsSessionContextValue>(
    () => ({
      openSessionId,
      openSessionChannelId,
      openedAt,
      sessionGroups,
      isLoading: messagesLoading,
      startSession,
      endSession,
      isEnding,
      showSmsSwitch,
      hasOpenSession,
      smsDisabledReason,
      sendersByChannelId,
      requesterFirstName,
    }),
    [
      showSmsSwitch,
      hasOpenSession,
      smsDisabledReason,
      openSessionId,
      openSessionChannelId,
      openedAt,
      sessionGroups,
      messagesLoading,
      startSession,
      endSession,
      isEnding,
      sendersByChannelId,
      requesterFirstName,
    ],
  );

  return <SmsSessionContext.Provider value={value}>{children}</SmsSessionContext.Provider>;
};
